import { useState } from 'react';
import { Video, PhoneOutgoing } from 'lucide-react';
import { meetingJoinAPI } from '../services/api.js';

// "Join a Meeting" admin card. An admin pastes a Google Meet / Zoom phone DIAL-IN
// number + PIN and clicks "Join Call Now"; the backend dispatches an AI (this
// tenant's persona/panel) into a fresh room, which dials the meeting + DTMFs the
// PIN and converses AS a participant. NO phone caller is bridged in — this is the
// clean, standalone version of the join_meet access code (which forced the human
// caller to stay on the line, causing echo/feedback).
//
// Mirrors AccessCodesEditor styling: a `card` with field-input inputs + a primary
// action button. Admin-gated on the server (403 for non-admins).
export default function JoinMeetingCard() {
  const [meetDialIn, setMeetDialIn] = useState('');
  const [meetPin, setMeetPin] = useState('');
  const [meetLabel, setMeetLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [joinedAt, setJoinedAt] = useState(null);

  const validate = () => {
    if (!/^\+[1-9]\d{6,14}$/.test(meetDialIn.trim())) {
      return 'Enter the meeting dial-in number in E.164 format (e.g. +14134892965).';
    }
    if (meetPin.trim() && !/^[0-9*# -]{2,40}$/.test(meetPin.trim())) {
      return 'PIN: digits only (spaces/dashes/# allowed, e.g. 789 613 622).';
    }
    return null;
  };

  const join = async () => {
    const msg = validate();
    if (msg) { setError(msg); return; }
    setBusy(true);
    setError('');
    try {
      await meetingJoinAPI.join({
        meetDialIn: meetDialIn.trim(),
        ...(meetPin.trim() ? { meetPin: meetPin.trim() } : {}),
        ...(meetLabel.trim() ? { meetLabel: meetLabel.trim() } : {})
      });
      setJoinedAt(new Date());
    } catch (err) {
      setError(err.message || 'Could not join the meeting.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Video size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Join a meeting</h2>
        </div>
        {joinedAt && (
          <span className="text-xs text-stone-500">Dispatched {joinedAt.toLocaleTimeString()}</span>
        )}
      </header>

      <p className="text-sm text-stone-400 mb-4">
        Push your AI into a live Google Meet or Zoom as a standalone participant — it
        dials the meeting's phone line itself and joins the conversation. No one has to
        stay on the line.
      </p>

      <p className="text-xs text-stone-500 mb-4">
        In the meeting, open <span className="text-stone-300">Joining info</span> (Meet)
        or <span className="text-stone-300">Invite → Phone</span> (Zoom) and copy the
        dial-in number and PIN from "Join by phone".
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}
      {joinedAt && !error && (
        <p className="mb-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200">
          The AI is joining the meeting now — it'll dial in, enter the PIN, and introduce itself.
        </p>
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            className="field-input"
            placeholder="Meeting dial-in number (+14134892965)"
            value={meetDialIn}
            onChange={(e) => setMeetDialIn(e.target.value)}
          />
          <input
            className="field-input"
            placeholder="Meeting PIN (e.g. 789 613 622)"
            value={meetPin}
            onChange={(e) => setMeetPin(e.target.value)}
            maxLength={40}
          />
        </div>
        <input
          className="field-input"
          placeholder="Meeting label (optional, e.g. Q3 partner sync)"
          value={meetLabel}
          onChange={(e) => setMeetLabel(e.target.value)}
          maxLength={120}
        />

        <button type="button" className="btn-primary" onClick={join} disabled={busy}>
          <PhoneOutgoing size={14} /> {busy ? 'Joining…' : 'Join Call Now'}
        </button>
      </div>
    </section>
  );
}
